/** Replay-adjacent surfaces: sharing, re-runs, live devtools, drill-downs. */
const en = {
  share: {
    title: 'Share this replay',
    body: 'Anyone with this link can watch the replay. No account needed. Revoke it to disable access.',
    revokeFailed: 'Couldn’t revoke the link.',
    revoking: 'Revoking…',
    revoke: 'Revoke link',
    creating: 'Creating…',
    create: 'Create public link',
  },
  rerun: {
    title: 'Re-run this scenario',
    body: (steps: number) =>
      `${steps} recorded steps will be replayed on the live site. Open the link below where the ReplayOps SDK is installed. The SDK shows a confirmation card and then plays the scenario. The run is recorded as a new session, linked to this one.`,
    maskedNote:
      'Masked fields (passwords, cards) are typed with synthetic test data. The recording never contained the real values. The link stops working if you revoke the session’s share link.',
    failed: 'Could not create the re-run link.',
    copied: 'Copied ✓',
    copyLink: 'Copy link',
  },
  drilldown: {
    columns: {
      user: 'User',
      page: 'Page',
      duration: 'Duration',
      errors: 'Errors',
      frustration: 'Frustration',
      started: 'Started',
    },
  },
  devTools: {
    network: 'Network',
    console: 'Console',
    errors: 'Errors',
    perf: 'Perf',
    waiting: 'Waiting for live activity…',
    noRequests: 'No requests yet.',
    noConsole: 'No console output.',
    noErrors: 'No errors. 🎉',
    graphql: 'GraphQL',
    request: 'Request',
    response: 'Response',
    fps: 'FPS',
    domNodes: 'DOM nodes',
    memory: 'Memory',
  },
  credentials: {
    projectId: 'Project ID',
    ingestKey: 'Ingest key',
    publicKey: 'Public key',
    setupLink: 'Set-password link',
  },
  page: {
    loading: 'Loading replay…',
    rerunHint: 'Replay this scenario on the live site',
    annotateHint: 'Annotate this frame and open a ticket',
    bugReport: 'Bug report',
    screenshotUnavailable: 'Screenshot capture unavailable, annotate freely.',
    ticketFailed: 'Could not create the ticket. Please try again.',
    reportedFrom: 'Reported from a session replay.',
    direct: 'Direct',
    details: {
      visitor: 'Visitor',
      browser: 'Browser',
      os: 'OS',
      device: 'Device',
      location: 'Location',
      ip: 'IP address',
      actions: 'Actions',
      actionsHint: 'User actions: clicks, navigations and errors (not raw rrweb events)',
      duration: 'Duration',
      referrer: 'Referrer',
      release: 'Release',
      environment: 'Environment',
      entryUrl: 'Entry URL',
    },
    tabs: {
      timeline: 'Timeline',
      console: 'Console',
      network: 'Network',
      errors: 'Errors',
      vitals: 'Vitals',
      correlate: 'Correlate',
      dom: 'DOM',
      backend: 'Backend',
      perf: 'Perf',
      steps: 'Steps',
    },
    netTypes: { all: 'All', xhr: 'XHR', js: 'JS', css: 'CSS', img: 'Images', font: 'Fonts', media: 'Media', other: 'Other' } as Record<string, string>,
    logLevels: { all: 'All', log: 'Logs', info: 'Info', warn: 'Warnings', error: 'Errors', debug: 'Debug', navigation: 'Navigation' } as Record<string, string>,
    noConsole: 'No console output.',
    filterNetwork: 'Filter network entries',
    noNetwork: 'No network activity.',
    jumpToRequest: 'Jump to this request',
    general: 'General',
    graphql: 'GraphQL',
    traceId: 'Trace id',
    reqHeaders: 'Request headers',
    reqPayload: 'Request payload',
    resHeaders: 'Response headers',
    resPayload: 'Response payload',
    noErrors: 'No errors in this session. 🎉',
    resolving: 'Resolving…',
    resolve: 'Resolve with sourcemap',
    noVitals: 'No web-vitals captured.',
    loadingLinks: 'Loading links…',
    clearAll: 'Clear all',
    selectAll: 'Select all',
    copied: 'Copied ✓',
    copySteps: 'Copy steps',
    noSteps: 'No reproducible steps in this session.',
    askingCoverage: 'Asking the coverage tool…',
    coverOpsNotIncluded:
      'CoverOps is connected, but your package does not include the CoverOps integration. It is included from Business, and an add-on on Pro.',
    regenerating: 'Regenerating…',
    regenerate: 'Regenerate',
    summaryFailed: 'Couldn’t generate a summary, try again.',
    ai: 'AI',
    heuristicNoKey: 'Heuristic (no AI key)',
    generating: 'Generating…',
    generateSummary: 'Generate AI summary',
    aiSummary: 'AI Summary',
    noSummaryYet: 'No summary yet. Let the AI watch this session and write up what happened.',
    noPerf: 'No performance samples for this session.',
    avgFps: 'Avg FPS',
    minFps: 'Min FPS',
    peakDom: 'Peak DOM nodes',
    peakMemory: 'Peak memory',
  },
  tester: {
    typeThenEnter: 'Type, then Enter',
    tools: {
      arrow: 'Arrow',
      box: 'Box',
      circle: 'Circle',
      pen: 'Pen',
      text: 'Text. Click on the image to type',
      move: 'Move. Drag a drawn shape',
      undo: 'Undo',
      clear: 'Clear',
    },
    title: 'Title',
    whatsWrong: "What's wrong?",
    describe: 'Describe the bug…',
    context: { url: 'URL', browser: 'Browser', os: 'OS', jsErrors: 'JS errors' },
    creating: 'Creating…',
    create: 'Create ticket',
  },
  publicReplay: {
    shared: 'Shared session replay',
    loading: 'Loading replay…',
    unavailable: 'Link unavailable',
    unavailableBody: 'This share link is invalid or has been revoked.',
    browser: 'Browser',
    device: 'device',
  },
  integrationField: {
    loadFailedStatus: (status: number) => `Could not load the list (HTTP ${status}).`,
    loadFailed: 'Could not load the list. Is the server reachable?',
    createFailed: 'Could not create it.',
    pickOrType: 'Pick from the list, or type a value.',
    reload: 'Reload the list',
  },
};

const tr: typeof en = {
  share: {
    title: 'Bu kaydı paylaş',
    body: 'Bu bağlantıya sahip herkes kaydı izleyebilir. Hesap gerekmez. Erişimi kapatmak için iptal edin.',
    revokeFailed: 'Bağlantı iptal edilemedi.',
    revoking: 'İptal ediliyor…',
    revoke: 'Bağlantıyı iptal et',
    creating: 'Oluşturuluyor…',
    create: 'Herkese açık bağlantı oluştur',
  },
  rerun: {
    title: 'Bu senaryoyu yeniden çalıştır',
    body: (steps: number) =>
      `Kaydedilen ${steps} adım canlı sitede yeniden oynatılacak. Aşağıdaki bağlantıyı ReplayOps SDK’sının kurulu olduğu yerde açın. SDK bir onay kartı gösterir ve ardından senaryoyu oynatır. Koşum, buna bağlı yeni bir oturum olarak kaydedilir.`,
    maskedNote:
      'Maskelenmiş alanlar (parolalar, kartlar) sentetik test verisiyle yazılır. Kayıt gerçek değerleri hiç içermedi. Oturumun paylaşım bağlantısını iptal ederseniz bu bağlantı da çalışmaz.',
    failed: 'Yeniden koşum bağlantısı oluşturulamadı.',
    copied: 'Kopyalandı ✓',
    copyLink: 'Bağlantıyı kopyala',
  },
  drilldown: {
    columns: {
      user: 'Kullanıcı',
      page: 'Sayfa',
      duration: 'Süre',
      errors: 'Hata',
      frustration: 'Zorlanma',
      started: 'Başlangıç',
    },
  },
  devTools: {
    network: 'Ağ',
    console: 'Konsol',
    errors: 'Hatalar',
    perf: 'Performans',
    waiting: 'Canlı hareket bekleniyor…',
    noRequests: 'Henüz istek yok.',
    noConsole: 'Konsol çıktısı yok.',
    noErrors: 'Hata yok. 🎉',
    graphql: 'GraphQL',
    request: 'İstek',
    response: 'Yanıt',
    fps: 'FPS',
    domNodes: 'DOM düğümü',
    memory: 'Bellek',
  },
  credentials: {
    projectId: 'Proje kimliği',
    ingestKey: 'Veri alım anahtarı',
    publicKey: 'Genel anahtar',
    setupLink: 'Parola belirleme bağlantısı',
  },
  page: {
    loading: 'Kayıt yükleniyor…',
    rerunHint: 'Bu senaryoyu canlı sitede yeniden oynat',
    annotateHint: 'Bu kareyi işaretle ve talep aç',
    bugReport: 'Hata bildirimi',
    screenshotUnavailable: 'Ekran görüntüsü alınamadı, serbestçe işaretleyin.',
    ticketFailed: 'Talep oluşturulamadı. Lütfen tekrar deneyin.',
    reportedFrom: 'Bir oturum kaydından bildirildi.',
    direct: 'Doğrudan',
    details: {
      visitor: 'Ziyaretçi',
      browser: 'Tarayıcı',
      os: 'İşletim sistemi',
      device: 'Cihaz',
      location: 'Konum',
      ip: 'IP adresi',
      actions: 'Eylem',
      actionsHint: 'Kullanıcı eylemleri: tıklama, gezinme ve hatalar (ham rrweb olayları değil)',
      duration: 'Süre',
      referrer: 'Yönlendiren',
      release: 'Sürüm',
      environment: 'Ortam',
      entryUrl: 'Giriş URL’si',
    },
    tabs: {
      timeline: 'Zaman çizelgesi',
      console: 'Konsol',
      network: 'Ağ',
      errors: 'Hatalar',
      vitals: 'Web Vitals',
      correlate: 'İlişkilendir',
      dom: 'DOM',
      backend: 'Sunucu',
      perf: 'Performans',
      steps: 'Adımlar',
    },
    netTypes: { all: 'Tümü', xhr: 'XHR', js: 'JS', css: 'CSS', img: 'Görseller', font: 'Yazı tipleri', media: 'Medya', other: 'Diğer' },
    logLevels: { all: 'Tümü', log: 'Günlükler', info: 'Bilgi', warn: 'Uyarılar', error: 'Hatalar', debug: 'Hata ayıklama', navigation: 'Gezinme' },
    noConsole: 'Konsol çıktısı yok.',
    filterNetwork: 'Ağ kayıtlarını filtrele',
    noNetwork: 'Ağ hareketi yok.',
    jumpToRequest: 'Bu isteğe git',
    general: 'Genel',
    graphql: 'GraphQL',
    traceId: 'İz kimliği',
    reqHeaders: 'İstek başlıkları',
    reqPayload: 'İstek gövdesi',
    resHeaders: 'Yanıt başlıkları',
    resPayload: 'Yanıt gövdesi',
    noErrors: 'Bu oturumda hata yok. 🎉',
    resolving: 'Çözümleniyor…',
    resolve: 'Kaynak haritasıyla çözümle',
    noVitals: 'Web Vitals verisi yakalanmadı.',
    loadingLinks: 'Bağlantılar yükleniyor…',
    clearAll: 'Tümünü temizle',
    selectAll: 'Tümünü seç',
    copied: 'Kopyalandı ✓',
    copySteps: 'Adımları kopyala',
    noSteps: 'Bu oturumda yeniden üretilebilir adım yok.',
    askingCoverage: 'Kapsam aracına soruluyor…',
    coverOpsNotIncluded:
      'CoverOps bağlı, ancak paketiniz CoverOps entegrasyonunu içermiyor. Business ve üstünde dahil, Pro’da ek paket olarak alınabilir.',
    regenerating: 'Yeniden üretiliyor…',
    regenerate: 'Yeniden üret',
    summaryFailed: 'Özet üretilemedi, tekrar deneyin.',
    ai: 'AI',
    heuristicNoKey: 'Sezgisel (AI anahtarı yok)',
    generating: 'Üretiliyor…',
    generateSummary: 'AI özeti üret',
    aiSummary: 'AI özeti',
    noSummaryYet: 'Henüz özet yok. AI bu oturumu izleyip ne olduğunu yazsın.',
    noPerf: 'Bu oturum için performans örneği yok.',
    avgFps: 'Ort. FPS',
    minFps: 'Min FPS',
    peakDom: 'Tepe DOM düğümü',
    peakMemory: 'Tepe bellek',
  },
  tester: {
    typeThenEnter: 'Yazın, sonra Enter',
    tools: {
      arrow: 'Ok',
      box: 'Kutu',
      circle: 'Daire',
      pen: 'Kalem',
      text: 'Metin. Yazmak için görsele tıklayın',
      move: 'Taşı. Çizilmiş bir şekli sürükleyin',
      undo: 'Geri al',
      clear: 'Temizle',
    },
    title: 'Başlık',
    whatsWrong: 'Sorun ne?',
    describe: 'Hatayı anlatın…',
    context: { url: 'URL', browser: 'Tarayıcı', os: 'İşletim sistemi', jsErrors: 'JS hataları' },
    creating: 'Oluşturuluyor…',
    create: 'Talep oluştur',
  },
  publicReplay: {
    shared: 'Paylaşılan oturum kaydı',
    loading: 'Kayıt yükleniyor…',
    unavailable: 'Bağlantı kullanılamıyor',
    unavailableBody: 'Bu paylaşım bağlantısı geçersiz ya da iptal edilmiş.',
    browser: 'Tarayıcı',
    device: 'cihaz',
  },
  integrationField: {
    loadFailedStatus: (status: number) => `Liste yüklenemedi (HTTP ${status}).`,
    loadFailed: 'Liste yüklenemedi. Sunucuya erişilebiliyor mu?',
    createFailed: 'Oluşturulamadı.',
    pickOrType: 'Listeden seçin ya da bir değer yazın.',
    reload: 'Listeyi yenile',
  },
};

export const replay = { en, tr };
